import React, { useState, useEffect, useRef } from "react";
import { Form, InputGroup, Button } from "react-bootstrap";
import { Formik } from "formik";
import { ActionHandlerFunction, OrgInfo } from "@preveil-api";
import { Account, FormValidationLimits, RegexHelper, usePutOrgDisplayNameMutation, AdminUIActionTypes, Message, MessageAnchors, AdminSuccessMessages, MessageHandlerDisplayType, AdminErrorMessages } from "src/common";
import { Icon } from "src/components";
import * as yup from "yup";

type AllProps = {
    current_account: Account;
    org_info: OrgInfo;
    handleAction: ActionHandlerFunction;
};

type FormProps = {
    organization_name: string;
}

function AdminRenameComponent(props: AllProps) {
    const { current_account, org_info, handleAction } = props;
    const [edit_mode, setEditMode] = useState<boolean>(false);
    const [org_name, setOrgName] = useState<string>(org_info.org_name || "");
    const input_ref = useRef<HTMLInputElement>(null);
    const [renameOrganization] = usePutOrgDisplayNameMutation();

    const schema = yup.object().shape({
        organization_name: yup.string()
            .required(AdminErrorMessages.error_organization_name_required)
            .min(
                FormValidationLimits.ORGANIZATION_NAME_MIN_LENGTH,
                AdminErrorMessages.error_organization_name_min
            )
            .test("org-name-chars", AdminErrorMessages.error_organization_name_invalid_chars, (value) => {
                return !value || RegexHelper.testDisplayNameChars(value);
            })
    });

    // Description: Ensures cursor is in input field when "Rename" option is selected in row dropdown
    useEffect(() => {
        (edit_mode && !!input_ref.current) && input_ref.current.focus();
    }, [edit_mode]);

    // Description: Clear form and save name when successful
    function handleReset(new_name: string) {
        setOrgName(new_name);
        setEditMode(false);
    }

    // Description: Validate and Save name 
    function handleSubmit(form_values: FormProps) {
        const new_name = form_values.organization_name.trim();
        const account_ids = Account.getAccountIdentifiers(current_account);
        renameOrganization({
            account_ids,
            body: {
                entity_id: org_info.org_id,
                name: new_name
            }
        })
            .unwrap()
            .then(() => { // Success
                handleReset(new_name);
                handleAction({
                    actionType: AdminUIActionTypes.Refresh,
                    params: new Message(AdminSuccessMessages.success_organization_name_changed.replace(MessageAnchors.message_content, new_name))
                });
            })
            .catch((error) => {
                handleReset(org_info.org_name || "");
                handleAction({
                    actionType: AdminUIActionTypes.PageErrorMessage,
                    params: {
                        message: AdminErrorMessages.error_renaming_organization,
                        stack: error,
                        display_type: MessageHandlerDisplayType.toastr
                    }
                });
            });
    }

    return edit_mode ?
        <Formik
            validationSchema={schema}
            onSubmit={handleSubmit}
            initialValues={{
                organization_name: org_name,
            }}
        >
            {({ values, errors, touched, handleChange, handleSubmit, handleBlur, isValid }) => (
                <Form noValidate onSubmit={handleSubmit}>
                    <Form.Label htmlFor="organization_name_tb" visuallyHidden>Organization Name</Form.Label>
                    <InputGroup size="sm">
                        <Form.Control
                            id="organization_name_tb"
                            className="ps-2"
                            ref={input_ref}
                            autoFocus={true}
                            name="organization_name"
                            type="text"
                            placeholder={"Organization Name"}
                            value={values.organization_name}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            isInvalid={(!!errors.organization_name && touched.organization_name)} />
                        {
                            !!org_name &&
                            <>
                                <Button type="submit" disabled={!isValid} variant="icon" size="sm">
                                    <Icon className="ficon-check" />
                                </Button>
                                <Button variant="icon" size="sm" className="text-muted-light"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={(_e) => handleReset(org_info.org_name || "")}>
                                    <Icon className="ficon-x-circle" /></Button>
                            </>
                        }
                    </InputGroup>
                    <Form.Control.Feedback type="invalid"
                        className={`${!!errors.organization_name && touched.organization_name ? "d-block" : ""}`}>
                        {errors.organization_name}
                    </Form.Control.Feedback>
                </Form>)}
        </Formik> :
        <p>
            {org_name}<Button variant="link" size="sm"
                onClick={() => setEditMode(true)}
                data-tooltip-id="tb-tooltip" data-tooltip-content="Edit Organization Name" data-tooltip-place="right"
            >
                <Icon className="pv-icon-compose text-muted" />
            </Button>
        </p>;
}

export default React.memo(AdminRenameComponent);
